// src/components/service/pdfUpload.jsx (PdfUpload3 � storage tree preserved, no Firestore)
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';

import addFile from '../../assets/addFile.png';
import '../css/GPTApp.css';

import { storage, ref, uploadBytesResumable} from '../../Firebase';

const acceptedFileType = { 'application/pdf': ['.pdf'] };

// -------------------- helpers --------------------
const isPdf = (file) =>
    (file && file.type === 'application/pdf') ||
    (file && typeof file.name === 'string' && /\.pdf$/i.test(file.name));

const normalizeRelPath = (p = '') =>
    String(p).replace(/^[.\\/]+/, '').replace(/\\/g, '/');

// Firebase Storage forbids # [ ] * ? and collapses repeated slashes
const storageSafePath = (p = '') =>
    normalizeRelPath(p)
        .split('/')
        .map(seg => seg.replace(/[#[\]*?]/g, '_'))
        .join('/')
        .replace(/\/{2,}/g, '/')
        .replace(/\/+$/g, '');

// -------------------- DnD traversal (legacy webkit) --------------------
function readAllEntries(reader) {
    return new Promise((resolve) => {
        const entries = [];
        const read = () => {
            reader.readEntries(
                (batch) => {
                    if (!batch.length) return resolve(entries);
                    entries.push(...batch);
                    read();
                },
                () => resolve(entries)
            );
        };
        read();
    });
}

async function walkEntry(entry, prefix = '') {
    if (!entry) return [];

    if (entry.isFile) {
        return new Promise((resolve) => {
            entry.file(
                (file) => resolve([{ file, relativePath: `${prefix}${file.name}` }]),
                () => resolve([])
            );
        });
    }

    if (entry.isDirectory) {
        const nextPrefix = `${prefix}${entry.name}/`; // keep top folder
        const reader = entry.createReader();
        const children = await readAllEntries(reader);
        const out = [];
        for (const child of children) {
            const nested = await walkEntry(child, nextPrefix);
            out.push(...nested);
        }
        return out;
    }

    return [];
}

async function getFromDataTransfer_Webkit(items) {
    const entries = [];
    for (const item of items) {
        if (typeof item.webkitGetAsEntry === 'function') {
            const entry = item.webkitGetAsEntry();
            if (entry) entries.push(entry);
        }
    }
    const results = [];
    for (const entry of entries) {
        const files = await walkEntry(entry, '');
        results.push(...files);
    }
    return results;
}

// -------------------- DnD traversal (FS Access API) --------------------
async function traverseFSHandle(handle, prefix = '') {
    const out = [];
    if (!handle) return out;

    if (handle.kind === 'file') {
        const file = await handle.getFile();
        out.push({ file, relativePath: `${prefix}${file.name}` });
    } else if (handle.kind === 'directory') {
        const nextPrefix = `${prefix}${handle.name}/`; // keep top folder
        for await (const entry of handle.values()) {
            const nested = await traverseFSHandle(entry, nextPrefix);
            out.push(...nested);
        }
    }
    return out;
}

async function getFromDataTransfer_FS(items) {
    const results = [];
    for (const item of items) {
        if (typeof item.getAsFileSystemHandle === 'function') {
            const handle = await item.getAsFileSystemHandle();
            const files = await traverseFSHandle(handle, '');
            results.push(...files);
        }
    }
    return results;
}

// -------------------- component --------------------
function PdfUpload3({
    userID,
    projectName,
    basePath,                 // e.g. 'users/<uid>'
    onFilesUploaded,          // [{ name, relativePath, storagePath }]
    onStartLoading,
    onFinishLoading,
    onProgress,               // ({ percent, uploadedCount, totalCount, totalBytes, transferredBytes, files })
    maxFiles = 1000,
}) {
    const [error, setError] = useState('');
    const [uiFiles, setUiFiles] = useState([]);

    const totalsRef = useRef({ totalBytes: 0, transferredBytes: 0, uploadedCount: 0, totalCount: 0 });
    const bytesByRelRef = useRef(new Map());
    const uiStateRef = useRef([]);

    const effectiveBasePath = useMemo(
        () => (basePath ? String(basePath).replace(/\/+$/, '') : `users/${userID || 'anon'}`),
        [basePath, userID]
    );

    // ---------- progress ----------
    const emitProgress = () => {
        const { totalBytes } = totalsRef.current;
        const transferred = Array.from(bytesByRelRef.current.values()).reduce((a, b) => a + b, 0);
        totalsRef.current.transferredBytes = transferred;

        const uploadedCount = uiStateRef.current.filter((f) => f.status === 'done').length;
        totalsRef.current.uploadedCount = uploadedCount;

        const totalCount = totalsRef.current.totalCount || uiStateRef.current.length;
        const percent = totalBytes ? Math.round((transferred / totalBytes) * 100) : 0;

        onProgress?.({
            percent,
            uploadedCount,
            totalCount,
            totalBytes,
            transferredBytes: transferred,
            files: uiStateRef.current,
        });
    };

    const setOneUi = (rel, patch) => {
        setUiFiles((prev) => {
            const next = prev.map((it) => (it.relativePath === rel ? { ...it, ...patch } : it));
            uiStateRef.current = next;
            return next;
        });
    };

    // ---------- upload preserving structure ----------
    const handleUpload = async (filesWithPaths) => {
        const basePrefix = storageSafePath(`${effectiveBasePath}/${projectName}`);
        const uploaded = [];

        const list = filesWithPaths
            .map(({ file, relativePath }) => ({
                file,
                relativePath: storageSafePath(relativePath || file.webkitRelativePath || file.name),
            }))
            .filter(({ file }) => isPdf(file))
            .slice(0, maxFiles);

        if (!list.length) return uploaded;

        const uiInit = list.map(({ relativePath }) => ({ relativePath, percent: 0, status: 'queued' }));
        uiStateRef.current = uiInit;
        setUiFiles(uiInit);

        bytesByRelRef.current = new Map();
        const totalBytes = list.reduce((acc, { file }) => acc + (file?.size || 0), 0);
        totalsRef.current = { totalBytes, transferredBytes: 0, uploadedCount: 0, totalCount: uiInit.length };
        emitProgress();

        // Upload sequentially (easy to reason about progress); can be made concurrent if needed
        for (const { file, relativePath } of list) {
            const storagePath = `${basePrefix}/${relativePath}`; // <-- this creates the folder tree virtually
            const fileRef = ref(storage, storagePath);
            const rel = relativePath;

            try {
                setOneUi(rel, { status: 'uploading', percent: 0 });

                await new Promise((resolve, reject) => {
                    const task = uploadBytesResumable(fileRef, file, { contentType: 'application/pdf' });
                    task.on(
                        'state_changed',
                        (snapshot) => {
                            const { bytesTransferred, totalBytes: tb } = snapshot;
                            bytesByRelRef.current.set(rel, bytesTransferred);
                            const percent = tb ? Math.floor((bytesTransferred / tb) * 100) : 0;
                            setOneUi(rel, { percent, status: 'uploading' });
                            emitProgress();
                        },
                        (err) => {
                            bytesByRelRef.current.set(rel, file?.size || 0);
                            setOneUi(rel, { status: 'error', error: err?.message || 'Upload failed', percent: 100 });
                            emitProgress();
                            reject(err);
                        },
                        () => {
                            bytesByRelRef.current.set(rel, file?.size || 0);
                            setOneUi(rel, { percent: 100, status: 'done' });
                            emitProgress();
                            resolve();
                        }
                    );
                });

                uploaded.push({ name: file.name, relativePath: rel, storagePath: fileRef.fullPath });
            } catch (e) {
                console.error('Upload failed for', relativePath, e);
            }
        }

        return uploaded;
    };

    // ---------- onDrop ----------
    const onDrop = useCallback(
        async (acceptedFiles, _fileRejections, event) => {
            if (!projectName) {
                setError('Please select a contract/folder before uploading.');
                return;
            }
            onStartLoading?.();
            setError('');

            try {
                let filesWithPaths = [];

                // Prefer legacy webkit DnD traversal (very reliable for folder drops)
                const dtItems = event?.dataTransfer?.items ? Array.from(event.dataTransfer.items) : [];
                if (dtItems.length) {
                    const viaWebkit = await getFromDataTransfer_Webkit(dtItems);
                    if (viaWebkit.length) {
                        filesWithPaths = viaWebkit;
                    } else {
                        // Fallback to FS Access API if webkit traversal yielded nothing
                        const viaFS = await getFromDataTransfer_FS(dtItems);
                        filesWithPaths = viaFS;
                    }
                }

                // Final fallback: user clicked to select (supports folder selection via webkitdirectory)
                if (!filesWithPaths.length && acceptedFiles?.length) {
                    filesWithPaths = acceptedFiles.map((f) => ({
                        file: f,
                        relativePath: normalizeRelPath(f.path || f.webkitRelativePath || f.name),
                    }));
                }

                // PDFs only
                filesWithPaths = filesWithPaths.filter(({ file }) => isPdf(file));

                if (!filesWithPaths.length) {
                    setError('No PDF files found in your selection.');
                    emitProgress();
                    return;
                }

                const uploaded = await handleUpload(filesWithPaths);
                onFilesUploaded?.(uploaded);
            } catch (e) {
                console.error(e);
                setError('There was a problem uploading your files.');
            } finally {
                onFinishLoading?.();
            }
        },
        [projectName, effectiveBasePath, onStartLoading, onFinishLoading, maxFiles, onFilesUploaded, onProgress]
    );

    return (
        <div className="dropzone">
            <Dropzone
                onDrop={onDrop}
                accept={acceptedFileType}
                maxFiles={maxFiles}
                multiple
            >
                {({ getRootProps, getInputProps }) => (
                    <div
                        className="inp"
                        {...getRootProps({
                            onClick: projectName ? undefined : (e) => e.stopPropagation(),
                        })}
                    >
                        {/* Allow clicking to choose an entire folder (helps Firefox) */}
                        <input
                            {...getInputProps({
                                webkitdirectory: 'true',
                                directory: 'true',
                            })}
                        />
                        <p style={{ marginBottom: 8 }}>
                            {projectName
                                ? 'Drag & drop PDFs here, or click to select a folder/file(s).'
                                : 'Disabled (select a contract first)'}
                        </p>

                        <img
                            style={{ height: '45px', float: 'inherit', margin: '0px 0px 4px 0px' }}
                            src={addFile}
                            alt="AddFile"
                        />
                    </div>
                )}
            </Dropzone>

            {error && (
                <div style={{ color: '#b00020', marginTop: 8, fontSize: 12 }}>{error}</div>
            )}
        </div>
    );
}

export default PdfUpload3;
